<template>
  <div class="model-main-page">
    <!-- 顶部边界 -->
<div class="top-border">
  <!-- 左侧标题 -->
  <div class="corner-text">
    <i class="bx bx-buildings"></i>
    桥隧群数字孪生模型管理系统
  </div>

  <!-- 右侧按钮区 -->
  <div class="top-right">
    <button class="top-btn" @click="$router.push({ name: 'RoadNet' })">
      <i class="bx bx-home-alt"></i>
      回到首页
    </button>
  </div>
</div>

    <!-- 左侧边栏 -->
    <div class="left-border">
      <div class="sidebar-title">
        <i class="bx bx-git-branch"></i>
        桥梁选择
      </div>
      <div class="button-container">
        <button
          class="sidebar-button"
          :class="{ active: currentBridge === 'yangpu' }"
          @click="switchBridge('yangpu')"
        >
          <i class="bx bx-arch"></i> 杨浦大桥
        </button>
        <button
          class="sidebar-button"
          :class="{ active: currentBridge === 'lupu' }"
          @click="switchBridge('lupu')"
        >
          <i class="bx bx-arch"></i> 卢浦大桥
        </button>
        <button
          class="sidebar-button"
          :class="{ active: currentBridge === 'xupu' }"
          @click="switchBridge('xupu')"
        >
          <i class="bx bx-arch"></i> 徐浦大桥
        </button>
        <button
          class="sidebar-button"
          :class="{ active: currentBridge === 'nanpu' }"
          @click="switchBridge('nanpu')"
        >
          <i class="bx bx-arch"></i> 南浦大桥
        </button>
      </div>
    </div>

    <!-- 内容区域：three.js 场景挂在这里 -->
    <div class="content-area">
      <!-- 模型标题 -->
      <div class="bridge-title">
        <i :class="currentBridgeIcon"></i>
        {{ currentBridgeTitle }} - 3D模型
      </div>

      <!-- three.js 容器 -->
      <div class="model-container" ref="modelContainer"></div>

      <!-- 水印 -->
      <div class="watermark">3D MODEL</div>
    </div>

    <!-- 加载动画 -->
    <div v-if="showLoading" id="load">
      <div class="load-container">
        <div class="load-img-wrapper">
          <div class="circle-outer"></div>
          <div class="circle-inner"></div>
        </div>
        <div class="load-center">加载中</div>
      </div>
      <div class="loading-text">数字孪生系统初始化</div>
      <div class="loading-subtext">正在加载三维模型与传感器数据...</div>
    </div>

    <!-- 底部信息栏 -->
    <div class="info-bar">
      <div class="system-status">
        <span class="status-indicator"></span>
        <span class="status-text">
          系统状态: <strong>运行正常</strong>
        </span>
      </div>
      <div class="datetime">
        <i class="bx bx-time-five"></i>
        <span>{{ datetimeText }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

export default {
  name: 'ModelMain',
  data() {
    return {
      currentBridge: 'yangpu', // 当前桥梁
      showLoading: true,
      datetimeText: '',

      // three.js 对象
      scene: null,
      camera: null,
      renderer: null,
      controls: null,
      modelGroup: null,
      animationId: null,
      resizeHandler: null,
      datetimeTimer: null,
    };
  },
  computed: {
    currentBridgeTitle() {
      switch (this.currentBridge) {
        case 'yangpu':
          return '杨浦大桥';
        case 'lupu':
          return '卢浦大桥';
        case 'xupu':
          return '徐浦大桥';
        case 'nanpu':
          return '南浦大桥';
        default:
          return '杨浦大桥';
      }
    },
    currentBridgeIcon() {
      switch (this.currentBridge) {
        case 'yangpu':
          return 'bx bx-bridge';
        case 'lupu':
          return 'bx bx-arch';
        case 'xupu':
          return 'bx bx-road';
        case 'nanpu':
          return 'bx bx-shape-polygon';
        default:
          return 'bx bx-bridge';
      }
    },
  },
  mounted() {
    // 1. 读 URL 参数 ?bridge=xxx
    const bridge = this.$route.query.bridge;
    const valid = ['yangpu', 'lupu', 'xupu', 'nanpu'];
    if (bridge && valid.includes(bridge)) {
      this.currentBridge = bridge;
    }

    // 2. 初始化时间
    this.initDateTime();

    // 3. 初始化 three.js 场景
    this.initThree();

    // 4. 加载当前桥梁模型
    this.switchBridge(this.currentBridge);
  },
  beforeDestroy() {
    this.cleanupThree();
  },
  methods: {
    initDateTime() {
      const update = () => {
        const now = new Date();
        const options = {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        };
        this.datetimeText = now.toLocaleDateString('zh-CN', options);
      };
      update();
      this.datetimeTimer = setInterval(update, 1000);
    },

    initThree() {
      const container = this.$refs.modelContainer;
      if (!container) return;

      const width = container.clientWidth || window.innerWidth;
      const height = container.clientHeight || window.innerHeight;

      const scene = new THREE.Scene();
      scene.background = new THREE.Color('#87CEEB');

      // 模型组：每次切换桥梁时，先清空这里再加新模型
      const modelGroup = new THREE.Group();
      scene.add(modelGroup);

      // 坐标轴（默认隐藏）
      const axesHelper = new THREE.AxesHelper(100);
      axesHelper.visible = false;
      scene.add(axesHelper);

      // 灯光
      const directionalLight = new THREE.DirectionalLight(0xffffff, 3);
      directionalLight.position.set(-500, 300, 342);
      scene.add(directionalLight);

      const ambient = new THREE.AmbientLight(0xffffff, 0.1);
      scene.add(ambient);

      // 相机
      const camera = new THREE.PerspectiveCamera(30, width / height, 1, 10000);
      camera.position.set(-620, 135, 956);
      camera.lookAt(0, 0, 0);

      // 渲染器
      const renderer = new THREE.WebGLRenderer({ antialias: true });
      renderer.setSize(width, height);
      renderer.outputEncoding = THREE.sRGBEncoding;
      container.appendChild(renderer.domElement);

      // 轨道控制
      const controls = new OrbitControls(camera, renderer.domElement);
      controls.target.set(-218, 43, 384);
      controls.update();

      // 动画循环
      const animate = () => {
        this.animationId = requestAnimationFrame(animate);
        renderer.render(scene, camera);
      };
      animate();

      // 窗口缩放
      const onResize = () => {
        const w = container.clientWidth || window.innerWidth;
        const h = container.clientHeight || window.innerHeight;
        renderer.setSize(w, h);
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
      };
      window.addEventListener('resize', onResize);

      // 保存实例
      this.scene = scene;
      this.camera = camera;
      this.renderer = renderer;
      this.controls = controls;
      this.modelGroup = modelGroup;
      this.resizeHandler = onResize;
    },

    // 切换桥梁：更新 currentBridge + 重新加载模型
    switchBridge(bridge) {
      this.currentBridge = bridge;
      this.loadBridgeModel(bridge);
    },

    // 真正加载 glb 模型
    loadBridgeModel(bridge) {
      if (!this.scene || !this.modelGroup) return;

      // 1. 清空旧模型，避免内存泄漏
      while (this.modelGroup.children.length) {
        const child = this.modelGroup.children.pop();
        if (!child) break;
        child.traverse && child.traverse((obj) => {
          if (obj.isMesh) {
            obj.geometry && obj.geometry.dispose();
            if (Array.isArray(obj.material)) {
              obj.material.forEach((m) => m && m.dispose && m.dispose());
            } else if (obj.material) {
              obj.material.dispose && obj.material.dispose();
            }
          }
        });
        this.scene.remove(child);
      }

      const loader = new GLTFLoader();
      const urlMap = {
        yangpu: 'https://cdn.bridgetunnelgroup.site/models/yangpu.glb',
        lupu: 'https://cdn.bridgetunnelgroup.site/models/lupu.glb',
        xupu: 'https://cdn.bridgetunnelgroup.site/models/xupu.glb',
        nanpu: 'https://cdn.bridgetunnelgroup.site/models/nanpu.glb',
      };
      const url = urlMap[bridge] || urlMap.yangpu;

      this.showLoading = true;

      loader.load(
        url,
        (gltf) => {
          this.modelGroup.add(gltf.scene);
          // 给所有 mesh 打上 clickable 标记（延续你原先的逻辑）
          gltf.scene.traverse((child) => {
            if (child.isMesh) {
              child.userData.clickable = true;
            }
          });
          this.showLoading = false;
        },
        undefined,
        (error) => {
          console.error('加载模型失败：', error);
          this.showLoading = false;
        }
      );
    },

    cleanupThree() {
      if (this.animationId) cancelAnimationFrame(this.animationId);
      if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler);
      if (this.datetimeTimer) clearInterval(this.datetimeTimer);

      if (this.renderer) {
        this.renderer.dispose();
        if (this.renderer.domElement && this.renderer.domElement.parentNode) {
          this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
        }
      }

      if (this.scene) {
        this.scene.traverse((obj) => {
          if (obj.isMesh) {
            obj.geometry && obj.geometry.dispose();
            if (Array.isArray(obj.material)) {
              obj.material.forEach((m) => m && m.dispose && m.dispose());
            } else if (obj.material) {
              obj.material.dispose && obj.material.dispose();
            }
          }
        });
      }
    },
  },
};
</script>

<style scoped>
.model-main-page {
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background: linear-gradient(135deg, #0a1429, #1d365f);
  color: #e6f7ff;
  box-sizing: border-box;
}

/* 顶部栏整体保持布局：左右分布 */
.top-border {
  width: 100%;
  height: 70px;
  background: rgba(10, 25, 50, 0.75);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: space-between; /* 关键：标题居左，按钮居右 */
  padding: 0 24px;
  box-sizing: border-box;
  border-bottom: 1px solid rgba(100, 150, 255, 0.15);
  z-index: 1000;
}

/* 左侧标题 */
.corner-text {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #4dc2ff;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-shadow: 0 0 10px rgba(77, 194, 255, 0.5);
}

.corner-text i {
  font-size: 26px;
}

/* 右侧区域容器 */
.top-right {
  display: flex;
  align-items: center;
  gap: 14px;
}

/* 回到首页按钮 */
.top-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.3);
  background: rgba(15, 23, 42, 0.85);
  color: #e5f0ff;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s ease-out;
}

.top-btn i {
  font-size: 18px;
}

.top-btn:hover {
  background: rgba(37, 99, 235, 0.95);
  color: #ffffff;
  border-color: rgba(191, 219, 254, 0.9);
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.6);
}


/* 左侧边栏 */
.left-border {
  position: absolute;
  top: 70px;
  left: 0;
  width: 220px;
  bottom: 40px;
  background: rgba(15, 30, 60, 0.7);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: 900;
  display: flex;
  flex-direction: column;
  padding: 25px 0;
  box-sizing: border-box;
  border-right: 1px solid rgba(100, 150, 255, 0.15);
  box-shadow: 5px 0 15px rgba(0, 0, 0, 0.2);
  overflow-y: auto;
}

/* 标题框 */
.sidebar-title {
  background: linear-gradient(135deg, #2a75d1, #00c6ff);
  color: white;
  text-align: center;
  width: 85%;
  padding: 15px 0;
  font-size: 19px;
  font-weight: bold;
  border-radius: 12px;
  margin: 10px auto 35px;
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.3);
  position: relative;
  overflow: hidden;
  transition: transform 0.3s ease;
}

.sidebar-title i {
  margin-right: 6px;
}

/* 按钮容器 */
.button-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 85%;
  margin: 0 auto;
  padding: 10px 0;
}

/* 左侧按钮样式 */
.sidebar-button {
  background: linear-gradient(135deg, rgba(40, 60, 100, 0.8), rgba(25, 45, 85, 0.9));
  color: #d6eeff;
  border: none;
  border-radius: 10px;
  padding: 16px 0;
  font-size: 17px;
  font-weight: 600;
  text-align: center;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.sidebar-button i {
  font-size: 20px;
}

.sidebar-button:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4);
  color: #fff;
}

.sidebar-button.active {
  background: linear-gradient(135deg, #00c6ff, #2a75d1);
  color: #0c1b33;
  box-shadow: 0 0 25px rgba(0, 198, 255, 0.6);
  transform: translateY(-3px);
}

/* 内容区域：three 容器所在 */
.content-area {
  position: absolute;
  top: 70px;
  left: 220px;
  right: 0;
  bottom: 40px;
  overflow: hidden;
  z-index: 800;
  background: rgba(10, 20, 40, 0.3);
}

/* 模型容器：three 的 canvas 会被插到这里 */
.model-container {
  width: 100%;
  height: 100%;
  position: relative;
}

/* 模型标题 */
.bridge-title {
  position: absolute;
  top: 25px;
  left: 25px;
  z-index: 1000;
  background: rgba(0, 20, 50, 0.75);
  backdrop-filter: blur(5px);
  padding: 12px 25px;
  border-radius: 50px;
  font-size: 22px;
  font-weight: 700;
  color: #4dc2ff;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(100, 180, 255, 0.2);
  display: flex;
  align-items: center;
  gap: 12px;
}

.bridge-title i {
  font-size: 24px;
}

/* 水印 */
.watermark {
  position: absolute;
  bottom: 60px;
  right: 25px;
  color: rgba(100, 180, 255, 0.15);
  font-size: 120px;
  font-weight: 900;
  z-index: 5;
  pointer-events: none;
  transform: rotate(-15deg);
  user-select: none;
}

/* 加载动画 */
#load {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #0a1429, #1a325e);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.load-container {
  position: relative;
  width: 200px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.load-img-wrapper {
  position: absolute;
  width: 100%;
  height: 100%;
}

.circle-outer,
.circle-inner {
  position: absolute;
  border-radius: 50%;
  border: 4px solid rgba(0, 198, 255, 0.7);
  border-top-color: transparent;
  border-right-color: transparent;
  animation: rotate 2s linear infinite;
}

.circle-outer {
  width: 200px;
  height: 200px;
}

.circle-inner {
  width: 140px;
  height: 140px;
  top: 30px;
  left: 30px;
  animation-duration: 1.5s;
}

@keyframes rotate {
  to {
    transform: rotate(360deg);
  }
}

.load-center {
  position: absolute;
  width: 80px;
  height: 80px;
  background: rgba(10, 25, 50, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #4dc2ff;
  z-index: 10;
  border: 2px solid rgba(100, 180, 255, 0.3);
}

.loading-text {
  font-size: 22px;
  color: #4dc2ff;
  text-align: center;
  text-shadow: 0 0 10px rgba(77, 194, 255, 0.7);
  margin-top: 30px;
  font-weight: 600;
  letter-spacing: 1px;
}

.loading-subtext {
  font-size: 16px;
  color: #88bbff;
  text-align: center;
  margin-top: 15px;
  opacity: 0.8;
}

/* 底部信息栏 */
.info-bar {
  position: absolute;
  bottom: 0;
  left: 220px;
  right: 0;
  height: 40px;
  background: rgba(10, 25, 50, 0.7);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  padding: 0 25px;
  font-size: 15px;
  color: #aad5ff;
  z-index: 900;
  border-top: 1px solid rgba(100, 150, 255, 0.15);
  box-sizing: border-box;
}

.system-status {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-indicator {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #00ff9d;
  box-shadow: 0 0 10px #00ff9d;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(0, 255, 157, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(0, 255, 157, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(0, 255, 157, 0);
  }
}

.datetime {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* 响应式简化 */
@media (max-width: 1200px) {
  .left-border {
    width: 200px;
  }
  .content-area {
    left: 200px;
  }
  .info-bar {
    left: 200px;
  }
}
</style>
